import axios, { AxiosError } from 'axios';

import { payrollErrorCode, readPayrollError } from './payrollError';

/**
 * Payroll command refusals the UI explains to the member: a refused or blocked run, a change made
 * stale by another edit, a command still in flight or already failed. They are answers rather than
 * faults, so they go to their own RUM action instead of RUM errors.
 */
const PAYROLL_COMMAND_REFUSAL_CODES: ReadonlySet<string> = new Set([
    'COMMAND_REFUSED',
    'BATCH_REFUSED',
    'STALE_TOKEN',
    'COMMAND_IN_FLIGHT',
    'COMMAND_ALREADY_FAILED',
]);

export const PAYROLL_COMMAND_REFUSED_ACTION = 'payroll_command_refused';
/** The statuses the contract refuses with. A 5xx carrying one of these codes is still a fault. */
const REFUSAL_STATUSES: ReadonlySet<number> = new Set([409, 412, 422]);
const PAYROLL_V1_PATH = /\/payroll\/v1\//;
const ID_SEGMENT = /\/(?:[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}|\d+)(?=\/|$)/gi;

interface PayrollCommandRefusal {
    code: string;
    reason: string | null;
    status: number;
    method: string;
    path_group: string;
}

/** The route with its ids replaced, so refusals group by endpoint rather than by payroll. */
const pathGroupOf = (url: string): string => url.split('?')[0].replaceAll(ID_SEGMENT, '/:id');

/** Only a reason the refusal contract names is sent; an unknown one could carry words. */
const reasonOf = (error: AxiosError): string | null => readPayrollError(error)?.reason ?? null;

/** The refusal's attributes when this is a payroll v1 write refused with a code the UI explains. */
export const payrollCommandRefusalOf = (error: unknown): PayrollCommandRefusal | null => {
    if (!axios.isAxiosError(error) || !error.response) {
        return null;
    }
    const method = (error.config?.method ?? 'get').toUpperCase();
    const url = error.config?.url ?? '';
    const code = payrollErrorCode(error);

    if (
        method === 'GET' ||
        !PAYROLL_V1_PATH.test(url) ||
        !REFUSAL_STATUSES.has(error.response.status) ||
        !code ||
        !PAYROLL_COMMAND_REFUSAL_CODES.has(code)
    ) {
        return null;
    }

    return { code, reason: reasonOf(error), status: error.response.status, method, path_group: pathGroupOf(url) };
};
